import React, { useState, useEffect, useContext } from 'react'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import Loader from 'components/general/Loader'
import PaymentMethodCard from 'components/provider/PaymentMethodCard'
import PaymentsTable from 'components/provider/PaymentsTable'
import SelectPlan from 'components/provider/SelectPlan'
import moment from 'moment'
import DeliveryCard from 'components/provider/DeliveryCard'
import BillingDeliveryCard from 'components/provider/BillingDeliveryCard'
import BillingSetupPrompts from 'components/provider/BillingSetupPrompts'

function Billing() {
    const api = new ProviderAPI()

    const [company, setCompany] = useState(null)
    const [currentBalance, setCurrentBalance] = useState(null)
    const [payments, setPayments] = useState([])
    const [paymentMethod, setPaymentMethod] = useState(null)
    const [loading, setLoading] = useState(true)
    const [alert, setAlert] = useState(false)
    const [showDeliveryDialog, setShowDeliveryDialog] = useState(false)
    const [paymentOpenSignal, setPaymentOpenSignal] = useState(0)

    useEffect(() => {
        getDetails()
    }, [])

    async function getDetails() {
        try {
            const company = await api.getCompany()
            console.log('company: ', company)

            const isSolar = company.productsAvailable && company.productsAvailable['Solar']

            let balanceDelivery = null
            if (isSolar && company.planType === 'topup') {
                balanceDelivery = await api.getCurrentBalance(company.companyId)
            }

            const payments = await api.getPayments(company.companyId)
            console.log('payments: ', payments)
            payments.sort((a, b) => {
                return moment(b.date).valueOf() - moment(a.date).valueOf()
            })

            let paymentMethod = await api.getPaymentMethod(company.companyId)
            console.log('payment method: ', paymentMethod)

            setCompany(company)
            setCurrentBalance(balanceDelivery ? balanceDelivery.balance : 0)
            setPayments(payments)
            setPaymentMethod(paymentMethod)
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    function showSuccessAlert(text) {
        setAlert(
            <ReactBSAlert
                success
                style={{ display: 'block', marginTop: '-100px', color: '#229626' }}
                title='Success'
                onConfirm={() => setAlert(false)}
                onCancel={() => setAlert(false)}
                confirmBtnBsStyle='success'
                confirmBtnText='Ok'
                btnSize=''
                confirmBtnStyle={{
                    marginRight: undefined,
                    borderColor: undefined,
                    boxShadow: undefined,
                    color: 'white',
                }}
            >
                {text}
            </ReactBSAlert>
        )
    }

    return (
        <>
            {/* Delivery dialog when plan set but no deliveryDetails */}
            {company && showDeliveryDialog && (
                <DeliveryCard
                    open={showDeliveryDialog}
                    onClose={() => setShowDeliveryDialog(false)}
                    companyDetails={company}
                    updateCompany={(updated) => setCompany(updated)}
                />
            )}
            <Container
                maxWidth={false}
                component={Box}
                marginTop='2rem'
                //classes={{ root: classes.containerRoot }}
            >
                {alert}
                {!loading ? (
                    <>
                        {(() => {
                            const isSolar = company.productsAvailable && company.productsAvailable['Solar']
                            const hasPlan = company.planDetails && company.planDetails.maxLeads && company.planDetails.maxLeads > 0

                            // Non-solar: simple billing view (card + payment history)
                            if (!isSolar) {
                                const missingPaymentMethod = !paymentMethod
                                return (
                                    <>
                                        <BillingSetupPrompts
                                            missingPaymentMethod={missingPaymentMethod}
                                            onSetupPayment={() => setPaymentOpenSignal(s => s + 1)}
                                        />
                                        <Grid container justifyContent='flex-start' spacing={3}>
                                            <Grid item xs={12} md={5}>
                                                <PaymentMethodCard
                                                    companyDetails={company}
                                                    paymentMethod={paymentMethod}
                                                    showSuccessAlert={(text) => showSuccessAlert(text)}
                                                    openSignal={paymentOpenSignal}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={7}>
                                                <PaymentsTable
                                                    companyId={company.companyId}
                                                    payments={payments}
                                                    onPaymentComplete={() => getDetails()}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                )
                            }

                            // Solar with active plan
                            if (hasPlan) {
                                const missingDeliveryMethod = !(company.deliveryDetails && company.deliveryDetails.deliveryMethod)
                                const missingPaymentMethod = !paymentMethod
                                return (
                                    <>
                                        <BillingSetupPrompts
                                            missingDeliveryMethod={missingDeliveryMethod}
                                            missingPaymentMethod={missingPaymentMethod}
                                            onSetupDelivery={() => setShowDeliveryDialog(true)}
                                            onSetupPayment={() => setPaymentOpenSignal(s => s + 1)}
                                        />
                                        <Grid container justifyContent='flex-start' spacing={3}>
                                            <Grid item xs={12} md={5}>
                                                <Box display='flex' flexDirection='column'>
                                                    <PaymentMethodCard
                                                        companyDetails={company}
                                                        paymentMethod={paymentMethod}
                                                        showSuccessAlert={(text) => showSuccessAlert(text)}
                                                        openSignal={paymentOpenSignal}
                                                    />
                                                    <BillingDeliveryCard
                                                        companyDetails={company}
                                                        paymentMethod={paymentMethod}
                                                        updateCompany={(updated) => setCompany(updated)}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} md={7}>
                                                <PaymentsTable
                                                    companyId={company.companyId}
                                                    payments={payments}
                                                    onPaymentComplete={() => getDetails()}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                )
                            }

                            // Solar without plan
                            return (
                                <Grid container justifyContent='flex-start'>
                                    <Grid item xs={12}>
                                        <SelectPlan 
                                            companyDetails={company}
                                            updateCompany={() => getDetails()}
                                            showSuccessAlert={(text) => showSuccessAlert(text)}
                                        />
                                    </Grid>
                                </Grid>
                            )
                        })()}
                    </>
                ) : (
                    <Grid container justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='center' alignItems='center' style={{height: '20vh'}}>
                                <Loader />
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </>
    )
}

export default Billing